<script setup lang="ts">
import { RelationStatus, ModerationStatus } from '@roadfootage/api-client'
import type { StaffIncidentReportRelationDto } from '@roadfootage/api-client'
import { formatDate, formatIncidentDateTime, formatNumber } from '@/utils/format'
import { shortReportRef } from '@/utils/report-ref'
import { formatGap } from '@/utils/relation-review'
import type { RelationDecision } from '@/utils/relation-review'
import RelationPairMap from './RelationPairMap.vue'

// Staff view of the reports an uploader says show the same incident (RF-133). The panel flags a pair that is further apart
// in distance or time than the configured limits. While a relation is pending, staff keep it or reject it; approving the
// report applies that.
export interface RelationPanelReport {
  incidentDateTime?: Date | string | null
  incidentTimezone?: string | null
  address?: { formattedAddress?: string | null } | null
  location?: { coordinates?: number[] } | null
}

defineProps<{
  relations: StaffIncidentReportRelationDto[]
  /** The report under review. */
  report: RelationPanelReport | null | undefined
}>()

const decisions = defineModel<Record<string, RelationDecision>>('decisions', { required: true })

const options = [
  { label: 'Keep link', value: 'keep' },
  { label: 'Reject link', value: 'reject' }
]

function setDecision(id: string, value: RelationDecision) {
  decisions.value = { ...decisions.value, [id]: value }
}

const decisionOf = (relation: StaffIncidentReportRelationDto): RelationDecision =>
  decisions.value[relation.id as string] ?? 'keep'

function statusText(relation: StaffIncidentReportRelationDto): string {
  if (relation.status === RelationStatus.Pending) {
    return decisionOf(relation) === 'reject' ? 'WILL BE REJECTED ON SAVE' : 'PENDING'
  }
  const label = relation.status === RelationStatus.Approved ? 'APPROVED' : 'REJECTED'
  const date = formatDate(relation.reviewedAt)
  return date ? `${label} ${date}` : label
}

function statusColor(relation: StaffIncidentReportRelationDto): string {
  if (relation.status === RelationStatus.Pending) {
    return decisionOf(relation) === 'reject' ? 'negative' : 'warning'
  }
  return relation.status === RelationStatus.Approved ? 'positive' : 'grey-7'
}

function flagText(relation: StaffIncidentReportRelationDto): string | null {
  if (relation.isFarApart && relation.isFarInTime) {
    return 'Further apart in distance and time than usual'
  }
  if (relation.isFarApart) return 'Further apart in distance than usual'
  if (relation.isFarInTime) return 'Further apart in time than usual'
  return null
}

// GeoJSON is [longitude, latitude]; Leaflet wants [latitude, longitude].
function latLng(location: { coordinates?: number[] } | null | undefined): [number, number] | null {
  const c = location?.coordinates
  if (!c || c.length < 2) return null
  return [c[1], c[0]]
}
</script>

<template>
  <div v-if="relations.length > 0" data-testid="relation-panel">
    <q-banner
      v-for="relation in relations"
      :key="relation.id"
      dense
      rounded
      class="bg-blue-1 text-grey-10 q-mt-sm"
      data-testid="relation-item"
    >
      <template #avatar>
        <q-icon name="mdi-link-variant" color="primary" />
      </template>

      <div class="text-weight-bold text-uppercase" data-testid="relation-title">
        Same incident as {{ shortReportRef(relation.otherIncidentReportId as string) }}
        <q-badge :color="statusColor(relation)" class="q-ml-xs" data-testid="relation-status">
          {{ statusText(relation) }}
        </q-badge>
      </div>

      <div
        v-if="relation.otherModerationStatus !== ModerationStatus.Approved"
        class="text-caption text-grey-8"
        data-testid="relation-other-status"
      >
        The other report is {{ relation.otherModerationStatus?.toLowerCase() }}, so it is not shown
        publicly yet.
      </div>

      <RelationPairMap
        v-if="latLng(report?.location) && latLng(relation.otherLocation)"
        class="q-mt-xs"
        :here="latLng(report?.location)!"
        :there="latLng(relation.otherLocation)!"
      />

      <div class="q-mt-xs" data-testid="relation-this">
        This:
        {{ formatIncidentDateTime(report?.incidentDateTime, report?.incidentTimezone) }}
        · {{ report?.address?.formattedAddress ?? 'Location unknown' }}
      </div>
      <div data-testid="relation-other">
        {{ shortReportRef(relation.otherIncidentReportId as string) }}:
        {{ formatIncidentDateTime(relation.otherIncidentDateTime, relation.otherIncidentTimezone) }}
        · {{ relation.otherFormattedAddress ?? 'Location unknown' }}
      </div>
      <div class="text-weight-medium" data-testid="relation-distance">
        {{ formatNumber(relation.distanceMetres ?? 0) }} m apart ·
        {{ formatGap(relation.timeGapMinutes ?? 0) }} apart
      </div>
      <div
        v-if="flagText(relation)"
        class="text-negative text-weight-medium"
        data-testid="relation-flag"
      >
        <q-icon name="mdi-flag" /> {{ flagText(relation) }} (flagged)
      </div>

      <template v-if="relation.status === RelationStatus.Pending">
        <q-option-group
          :model-value="decisionOf(relation)"
          :options="options"
          type="radio"
          inline
          dense
          data-testid="relation-decision"
          @update:model-value="setDecision(relation.id as string, $event as RelationDecision)"
        />
        <div class="text-caption text-grey-8">
          Approving the report approves the link once the other report is approved too.
        </div>
      </template>
    </q-banner>
  </div>
</template>
